import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  dashboardAPI,
  notificationsAPI,
  NOTIFICATIONS_CHANGED_EVENT,
} from '../../utils/api';
import { getEventTitle } from '../../utils/modelLabels';
import {
  getViewedLogIds,
  markLogViewed,
  markLogsViewed,
  VIEWED_LOGS_CHANGED_EVENT,
} from '../../utils/viewedLogs';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { IconBell } from '../icons/Icons';
import './Notifications.css';

interface RecentEvent {
  id: number;
  timestamp: string | null;
  model: string;
  area_id: number;
  area_name: string;
  camera_id: number;
  camera_name: string;
  screen_url: string | null;
}

interface SystemNotification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  meta: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string;
}

type FeedItem =
  | { kind: 'system'; sortAt: number; data: SystemNotification }
  | { kind: 'event'; sortAt: number; data: RecentEvent };

const EVENTS_LIMIT = 99;
const LIST_LIMIT = 10;
const POLL_MS = 30_000;
const MAX_BADGE = 99;

/** «5 минут назад» / «вчера, 14:32» — компактная подпись для списка. */
function formatRelativeTime(timestamp: string | null): string {
  if (!timestamp) return '—';
  const date = new Date(timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} ч назад`;

  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (diffHours < 48) return `вчера, ${time}`;

  return `${date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}, ${time}`;
}

function toSortMs(timestamp: string | null | undefined): number {
  if (!timestamp) return 0;
  const date = new Date(timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

/**
 * Колокольчик уведомлений в верхней панели.
 *
 * Системные уведомления (очередь камер и т.п.) + последние детекции.
 */
const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requireSubscription } = useSubscription();

  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<RecentEvent[]>([]);
  const [systemItems, setSystemItems] = useState<SystemNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [viewedIds, setViewedIds] = useState<Set<number>>(() => getViewedLogIds());
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const loadFeed = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) setLoading(true);

      const [eventsRes, systemRes] = await Promise.allSettled([
        dashboardAPI.getRecentEvents({ limit: EVENTS_LIMIT, offset: 0 }),
        notificationsAPI.list(30),
      ]);

      let hasAny = false;

      if (eventsRes.status === 'fulfilled' && eventsRes.value.success) {
        setEvents(eventsRes.value.events);
        hasAny = true;
      }

      if (systemRes.status === 'fulfilled' && systemRes.value.success) {
        setSystemItems(systemRes.value.notifications);
        hasAny = true;
      }

      // Ошибку показываем только если оба запроса упали
      if (!hasAny && (eventsRes.status === 'rejected' || systemRes.status === 'rejected')) {
        if (!options?.silent) setError(true);
      } else {
        setError(false);
      }
    } catch {
      if (!options?.silent) setError(true);
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFeed();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadFeed({ silent: true });
      }
    }, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadFeed({ silent: true });
      }
    };

    const onSystemChanged = () => {
      void loadFeed({ silent: true });
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onSystemChanged);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onSystemChanged);
    };
  }, [loadFeed]);

  useEffect(() => {
    const sync = () => setViewedIds(getViewedLogIds());
    window.addEventListener(VIEWED_LOGS_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(VIEWED_LOGS_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const feed = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [
      ...systemItems.map((data) => ({
        kind: 'system' as const,
        sortAt: toSortMs(data.created_at),
        data,
      })),
      ...events.map((data) => ({
        kind: 'event' as const,
        sortAt: toSortMs(data.timestamp),
        data,
      })),
    ];
    items.sort((a, b) => b.sortAt - a.sortAt);
    return items;
  }, [events, systemItems]);

  const unreadSystem = useMemo(
    () => systemItems.filter((item) => !item.read_at).length,
    [systemItems]
  );

  const unreadEvents = useMemo(
    () => events.filter((event) => !viewedIds.has(event.id)).length,
    [events, viewedIds]
  );

  const unreadCount = unreadSystem + unreadEvents;

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) void loadFeed({ silent: feed.length > 0 });
      return next;
    });
  };

  const handleOpenEvent = (event: RecentEvent) => {
    markLogViewed(event.id);
    setViewedIds(getViewedLogIds());
    setOpen(false);
    requireSubscription(() => navigate(`/area/${event.area_id}`));
  };

  const handleOpenSystem = async (item: SystemNotification) => {
    if (!item.read_at) {
      try {
        await notificationsAPI.markRead({ ids: [item.id] });
        setSystemItems((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n))
        );
      } catch {
        // ignore
      }
    }

    setOpen(false);
    const areaId = item.meta && typeof item.meta.area_id === 'number' ? item.meta.area_id : null;
    if (areaId) {
      requireSubscription(() => navigate(`/area/${areaId}`));
    }
  };

  const handleMarkAllRead = async () => {
    markLogsViewed(events.map((event) => event.id));
    setViewedIds(getViewedLogIds());
    if (unreadSystem > 0) {
      try {
        await notificationsAPI.markRead({ all: true });
        setSystemItems((prev) =>
          prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() }))
        );
      } catch {
        // ignore
      }
    }
  };

  return (
    <div className="notifications" ref={wrapRef}>
      <button
        type="button"
        className="topbar__icon-btn notifications__trigger"
        aria-label={
          unreadCount > 0 ? `Уведомления: ${unreadCount} новых` : 'Уведомления'
        }
        aria-expanded={open}
        aria-haspopup="menu"
        title="Уведомления"
        onClick={handleToggle}
      >
        <IconBell size={19} />
      </button>

      {unreadCount > 0 && (
        <span className="notifications__badge" aria-hidden="true">
          {unreadCount > MAX_BADGE ? `${MAX_BADGE}+` : unreadCount}
        </span>
      )}

      {open && (
        <div className="notifications__panel ui-menu" role="menu">
          <header className="notifications__head">
            <span className="notifications__title">Последние события</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notifications__mark-all"
                onClick={() => void handleMarkAllRead()}
              >
                Отметить прочитанными
              </button>
            )}
          </header>

          <div className="notifications__list ui-scroll">
            {loading ? (
              <div className="ui-loading">Загрузка…</div>
            ) : error ? (
              <div className="ui-empty">
                <span>Не удалось загрузить события</span>
                <button
                  type="button"
                  className="ui-btn ui-btn--outline ui-btn--sm"
                  onClick={() => void loadFeed()}
                >
                  Повторить
                </button>
              </div>
            ) : feed.length === 0 ? (
              <div className="ui-empty">
                <span className="ui-empty__title">Событий пока нет</span>
                <span>Детекции и системные сообщения появятся здесь</span>
              </div>
            ) : (
              feed.slice(0, LIST_LIMIT).map((item) => {
                if (item.kind === 'system') {
                  const n = item.data;
                  const isNew = !n.read_at;
                  return (
                    <button
                      key={`sys-${n.id}`}
                      type="button"
                      role="menuitem"
                      className={`notifications__item${
                        isNew ? ' notifications__item--new' : ''
                      }`}
                      onClick={() => void handleOpenSystem(n)}
                    >
                      <span className="notifications__dot" aria-hidden="true" />
                      <span className="notifications__item-body">
                        <span className="notifications__item-title">{n.title}</span>
                        {n.body && (
                          <span className="notifications__item-meta">{n.body}</span>
                        )}
                      </span>
                      <span className="notifications__item-time">
                        {formatRelativeTime(n.created_at)}
                      </span>
                    </button>
                  );
                }

                const event = item.data;
                const isNew = !viewedIds.has(event.id);
                return (
                  <button
                    key={`evt-${event.id}`}
                    type="button"
                    role="menuitem"
                    className={`notifications__item${
                      isNew ? ' notifications__item--new' : ''
                    }`}
                    onClick={() => handleOpenEvent(event)}
                  >
                    <span className="notifications__dot" aria-hidden="true" />
                    <span className="notifications__item-body">
                      <span className="notifications__item-title">
                        {getEventTitle(event.model)}
                      </span>
                      <span className="notifications__item-meta">
                        {event.area_name} • {event.camera_name}
                      </span>
                    </span>
                    <span className="notifications__item-time">
                      {formatRelativeTime(event.timestamp)}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <footer className="notifications__footer">
            <button
              type="button"
              className="notifications__all"
              onClick={() => {
                setOpen(false);
                const scrollToEvents = () => {
                  document
                    .getElementById('dashboard-events')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                };

                if (location.pathname === '/dashboard') {
                  window.requestAnimationFrame(scrollToEvents);
                  return;
                }

                navigate('/dashboard#events');
              }}
            >
              Все события
            </button>
          </footer>
        </div>
      )}
    </div>
  );
};

export default Notifications;
